import '../styles.scss';
import { useEffect, useMemo, useState } from 'react';
import OnboardingDialog from '../../../../common/onboarding-dialog';
import l from '../../../../../lang';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../../../config/routes';
import { usePayouts } from '../../../../../store/payouts';
import { FlowType, PayoutCreationStep } from '../../../../../store/payouts/interfaces';
import CountrySelection from './country-selection';
import BankAccountForm from './bank-account-form';
import { getPayoutWizardMenuItems, mapFieldPathValuesToCreatePayoutData } from '../utils';

const AccountData = () => {
  const history = useHistory();
  const {
    createPayout,
    startCreatePayout,
    setCreatePayoutData,
    setCreatePayoutStep,
    setCreatePayoutSubView,
    getRequiredFields,
    getBanks,
    validatePartialPayout,
    verifyOtpConfigured,
  } = usePayouts();
  const [country, setCountry] = useState(createPayout.createPayoutData.transferCountry || '');
  const [flowType, setFlowType] = useState<FlowType>(createPayout.createPayoutData.flowType || FlowType.B2C);
  const [submittedForValidation, setSubmittedForValidation] = useState(false);

  useEffect(() => {
    if (createPayout.step === PayoutCreationStep.NONE) {
      startCreatePayout();
    }
    setCreatePayoutStep(PayoutCreationStep.ACCOUNT_DATA);
    verifyOtpConfigured();
  }, []);

  useEffect(() => {
    if (createPayout.accountSubView === 'bank-data' && createPayout.createPayoutData.transferCountry) {
      getBanks(createPayout.createPayoutData.transferCountry);
    }
  }, [createPayout.accountSubView, createPayout.createPayoutData.transferCountry]);

  useEffect(() => {
    if (!submittedForValidation || !createPayout.partialValidation.success) {
      return;
    }

    setCreatePayoutStep(PayoutCreationStep.PAYOUT_DATA);
    history.push(Routes.PAYOUTS_STEP_PAYOUT_DATA);
  }, [submittedForValidation, createPayout.partialValidation.success]);

  const title = useMemo(() => {
    return `${l('payouts.create.title')} | ${l('payouts.create.accountData.menuTitle')}`;
  }, []);

  const handleOpenBankData = () => {
    setCreatePayoutData({ transferCountry: country, flowType });
    getRequiredFields(country, flowType);
    setCreatePayoutSubView('bank-data');
  };

  const handleSubmitBankData = (values: Record<string, string | number>) => {
    setCreatePayoutData(mapFieldPathValuesToCreatePayoutData(values));
    setSubmittedForValidation(true);
    validatePartialPayout(values);
  };

  const filteredFields = ['flow_type', 'country', 'currency_to_pay', 'amount', 'description'];

  const body =
    createPayout.accountSubView === 'country' ? (
      <CountrySelection
        country={country}
        flowType={flowType}
        onCountryChange={setCountry}
        onFlowTypeChange={setFlowType}
        onCancel={() => history.push(Routes.PAYOUTS)}
        onNext={handleOpenBankData}
      />
    ) : (
      <BankAccountForm
        loading={createPayout.requiredFields.loading}
        fields={createPayout.requiredFields.fields.filter(field => !filteredFields.includes(field.fieldPath))}
        bankOptions={createPayout.bankOptions}
        initialValues={createPayout.createPayoutData}
        onBack={() => setCreatePayoutSubView('country')}
        onSubmit={handleSubmitBankData}
        validating={createPayout.partialValidation.loading}
        backendErrorFields={createPayout.partialValidation.errorFields}
      />
    );

  return (
    <OnboardingDialog
      dividers={false}
      unpaddingContent
      unpaddingFooter
      fullScreenMobile
      type="md"
      bgColor="#F9FAFB"
      title={title}
      body={body}
      handleClose={() => history.push(Routes.PAYOUTS)}
      menuItems={getPayoutWizardMenuItems('account')}
    />
  );
};

export default AccountData;
